import { ICodeTaskDTO } from '../lib/interfaces/CodeTask';
import { CommentDTO } from '../lib/interfaces/Comment';
import { Course } from '../lib/interfaces/Course';
import { RecoverPasswordDTO } from '../lib/interfaces/RecoverPasswordDTO';
import { Task } from '../lib/interfaces/Task';
import {
	UserDTO,
	UserGoogleLogin,
	UserRegistrationDTO,
} from '../lib/interfaces/User';

export const getBaseDomain = () => {
	switch (process.env.REACT_APP_ENV) {
		case 'production':
			return 'https://cwt-fe.pages.dev';
		case 'staging':
			return 'https://main.cwt-fe.pages.dev';
		case 'local':
			return 'http://localhost:3000';
		default:
			return 'https://main.cwt-fe.pages.dev';
	}
};

const getBaseEnv = () => {
	switch (process.env.REACT_APP_ENV) {
		case 'production':
			return 'https://api.codeway.today/api';
		case 'staging':
			return 'https://stg-api.codeway.today/api';
		case 'local':
			return 'http://localhost:4000/api';
		default:
			return 'https://stg-api.codeway.today/api';
	}
};

export const BASE_URL = getBaseEnv();

const getDashboardBaseEnv = () => {
	switch (process.env.REACT_APP_ENV) {
		case 'production':
			return 'https://cwt-fe-dashboard.pages.dev';
		case 'staging':
			return 'https://main.cwt-fe-dashboard.pages.dev';
		case 'local':
			return 'http://localhost:3001';
		default:
			return 'http://localhost:3001';
	}
};

export const DASHBOARD_BASE_URL = getDashboardBaseEnv();

/**
 * List of Content-types.
 * You can add more if needed.
 *
 * @enum {string}
 */
export const contentTypes = {
	APPLICATION_JSON: 'application/json',
	MULTIPART_FORMDATA: 'multipart/form-data',
};

export const API_METHODS = {
	GET: 'GET',
	POST: 'POST',
	PATCH: 'PATCH',
	PUT: 'PUT',
	DELETE: 'DELETE',
};

/**
 * Creates header from data
 *
 * @param {(contentTypes|string)} contentType One of the types from contentTypes list or string
 * @param {boolean} [hasToken=true] - If true (by default) token will be added to Header object
 * @returns {object} Header object with "Content-Type" and "Authorization" fields.
 *
 */
export const createHeader = (contentType: string | null, hasToken = true) => {
	const headerResult: {
		Authorization?: string;
		['Content-Type']?: string;
	} = {};

	if (hasToken) {
		const token: string | null = localStorage.getItem('token');
		if (token) {
			// eslint-disable-next-line no-unused-expressions
			headerResult.Authorization = `Bearer ${JSON.parse(token)}`;
		}
	}

	headerResult['Content-Type'] = contentType || contentTypes.APPLICATION_JSON;

	return headerResult;
};

/**
 * Enpoints Constructor
 *
 */
export const endpoints = {
	auth: {
		getLoginApi: (data: UserDTO) => ({
			headers: createHeader(null, false),
			url: `${BASE_URL}/auth/login`,
			method: API_METHODS.POST,
			data,
		}),

		getGoogleLoginApi: (data: UserGoogleLogin) => ({
			headers: createHeader(null, false),
			url: `${BASE_URL}/auth/google-login`,
			method: API_METHODS.POST,
			data,
		}),

		getRegistrationApi: (data: UserRegistrationDTO) => ({
			headers: createHeader(null, false),
			url: `${BASE_URL}/auth/register`,
			method: API_METHODS.POST,
			data,
		}),

		activateUser: (token: string) => ({
			headers: createHeader(null, false),
			url: `${BASE_URL}/auth/activate/${token}`,
			method: API_METHODS.GET,
		}),

		resendUserActivation: () => ({
			headers: createHeader(null, true),
			url: `${BASE_URL}/auth/resend-activation-token`,
			method: API_METHODS.GET,
		}),

		getUserMe: () => ({
			headers: createHeader(null, true),
			url: `${BASE_URL}/user/me`,
			method: API_METHODS.GET,
		}),
		forgotPassword: (email: string) => ({
			headers: createHeader(null, false),
			url: `${BASE_URL}/auth/forgotPassword`,
			method: API_METHODS.POST,
			data: {
				email,
			},
		}),
		recoveryPassword: (data: RecoverPasswordDTO) => ({
			headers: createHeader(null, false),
			url: `${BASE_URL}/auth/recoverPassword`,
			method: API_METHODS.POST,
			data,
		}),
	},
	courses: {
		getCourses: (occupation: string) => ({
			headers: createHeader(null, true),
			url: `${BASE_URL}/course/occupation/${occupation}`,
			method: API_METHODS.GET,
		}),

		getCourseBySlug: (slug: string) => ({
			headers: createHeader(null, true),
			url: `${BASE_URL}/course/${slug}`,
			method: API_METHODS.GET,
		}),

		createCourse: (data: Course) => ({
			method: API_METHODS.POST,
			url: `${BASE_URL}/course`,
			headers: createHeader(null, true),
			data,
		}),

		updateCourse: (data: Course, id: number | undefined) => ({
			method: API_METHODS.PATCH,
			url: `${BASE_URL}/course/${id}`,
			headers: createHeader(null, true),
			data,
		}),
	},
	tasks: {
		getTaskById: (slug: string) => ({
			headers: createHeader(null, true),
			url: `${BASE_URL}/task/${slug}`,
			method: API_METHODS.GET,
		}),

		createTask: (data: Task) => ({
			method: API_METHODS.POST,
			url: `${BASE_URL}/task`,
			headers: createHeader(null, true),
			data,
		}),

		updateTask: (data: Task, id: number | undefined) => ({
			method: API_METHODS.PATCH,
			url: `${BASE_URL}/task/${id}`,
			headers: createHeader(null, true),
			data,
		}),

		toggleCompleteTask: (taskId: number) => ({
			method: API_METHODS.PATCH,
			url: `${BASE_URL}/user/complete-task/${taskId}`,
			headers: createHeader(null, true),
			data: {},
		}),
	},
	comments: {
		getTaskComments: (id: number) => ({
			headers: createHeader(null, true),
			url: `${BASE_URL}/task/${id}/comment`,
			method: API_METHODS.GET,
		}),
		createComment: (data: CommentDTO, id: number) => ({
			method: API_METHODS.POST,
			url: `${BASE_URL}/task/${id}/comment`,
			headers: createHeader(null, true),
			data,
		}),
		updateComment: (data: CommentDTO, id: number) => ({
			method: API_METHODS.PATCH,
			url: `${BASE_URL}/comment/${id}`,
			headers: createHeader(null, true),
			data,
		}),
		replyComment: (
			data: CommentDTO,
			taskId: number,
			commentId: number | undefined,
		) => ({
			method: API_METHODS.POST,
			url: `${BASE_URL}/task/${taskId}/comment/${commentId}`,
			headers: createHeader(null, true),
			data,
		}),
		deleteComment: (data: CommentDTO, id: number) => ({
			method: API_METHODS.DELETE,
			url: `${BASE_URL}/comment/${id}`,
			headers: createHeader(null, true),
			data,
		}),
	},
	utils: {
		uploadFile: (data: FormData) => ({
			// TODO: add division which file is uploaded (user or admin)
			method: API_METHODS.POST,
			url: `${BASE_URL}/common/upload-file/`,
			headers: createHeader('multipart/form-data', true),
			data,
		}),
	},
	quiz: {
		getQuizBySlug: (slug: string) => ({
			headers: createHeader(null, true),
			url: `${BASE_URL}/quiz/${slug}`,
			method: API_METHODS.GET,
		}),
	},
	codeTasks: {
		getCodeTaskBySlug: (slug: string) => ({
			headers: createHeader(null, true),
			url: `${BASE_URL}/code-task/${slug}`,
			method: API_METHODS.GET,
		}),
		createUserCodeTask: (data: ICodeTaskDTO) => ({
			method: API_METHODS.POST,
			url: `${BASE_URL}/code-task/save/user-code`,
			headers: createHeader(null, true),
			data,
		}),
	},
	products: {
		getAllProducts: () => ({
			headers: createHeader(null, true),
			url: `${BASE_URL}/product`,
			method: API_METHODS.GET,
		}),
		getProductBySlug: (slug: string) => ({
			headers: createHeader(null, true),
			url: `${BASE_URL}/product/${slug}`,
			method: API_METHODS.GET,
		}),
	},
	orders: {
		createOrder: (data: FormData) => ({
			method: API_METHODS.POST,
			url: `${BASE_URL}/order`,
			headers: createHeader(null, true),
			data,
		}),
		getOrderByNumber: (number: number) => ({
			headers: createHeader(null, true),
			url: `${BASE_URL}/order/${number}`,
			method: API_METHODS.GET,
		}),
	},
};
